<script setup lang="ts">
import { useFlowControlStore } from '@components/flow-controller'
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, ref } from 'vue'

import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import ActionCard from '@tix/ds/components/molecules/ActionCard.vue'
import type { Schema } from '@tix/openapi-sdk'

import { getEnv } from '@/helpers/environment'
import { getErrorForAction } from '@/helpers/flowActionErrors'
import { getPickTimeDisplayName } from '@/helpers/flowOptionLabel'
import { formatPickTimeValue } from '@/helpers/flowStepValue'

import { useBundleStepTracking } from '@/composables/useBundleStepTracking'

import ActionHeader from './ActionHeader.vue'
import type { ActionValidationError, SelectableItemBase } from './types'

type TimeItem = SelectableItemBase & {
  isSoldOut: boolean
}

const actionType: Schema<'action_pick_time'>['type'] = 'pick_time'
const env = getEnv()
const store = useFlowControlStore()
const { trackBundleStep } = useBundleStepTracking()

// Initialize from previous input if available (e.g. when navigating back)
const prevInput = store.currentActionPreviousInput
const selectedId = ref<string | null>((prevInput?.type === actionType && prevInput.input?.id) || null)
const validationError = ref<ActionValidationError | null>(null)

// Register input getter with the store
store.registerInputGetter(() => {
  if (!selectedId.value) {
    validationError.value = {
      type: 'client-side',
      message: 'Please select a time to continue.',
    }
    return { isValid: false }
  }

  // Reset errors on every submission attempt
  validationError.value = null

  return { isValid: true, input: { id: selectedId.value } }
})

// Register error handler for API errors (e.g., time became unavailable)
store.registerErrorHandler((apiError: unknown) => {
  const { message } = getErrorForAction(apiError, actionType)
  if (message) validationError.value = { type: 'server-side', message }
  return Boolean(message)
})

const action = computed(() => (store.action?.type === actionType && store.action) || null)

const title = computed(() => action.value?.meta.summary_title)
const prompt = computed(() => action.value?.meta.prompt)

const items = computed<TimeItem[]>(() => {
  const times = action.value?.options?.times ?? []

  return times
    .filter((time) => {
      if (time.status === 'unreleased') return false
      if (time.status === 'sold_out') return !env.config.web.hide_sold_out_sessions
      return true
    })
    .map((time) => ({
      id: time.id,
      title: getPickTimeDisplayName(time),
      isSoldOut: time.status === 'sold_out',
    }))
})

// TODO - Improve wording of user-facing error messaging - https://ticketure-team.atlassian.net/browse/TIC-5452
const errorMessage = computed<string | null>(() => {
  if (!action.value) return `Action is not a ${actionType} action`
  if (items.value.length === 0) return 'No available times to select.'
  if (validationError.value) return validationError.value.message ?? 'Validation error'
  return null
})

function isSelected(id: string): boolean {
  return selectedId.value === id
}

function handleSelect(id: string): void {
  validationError.value = null
  if (selectedId.value === id) return // re-click clears error but isn't a tracked change

  selectedId.value = id
  trackBundleStep(formatPickTimeValue(id, action.value?.options?.times))
}
</script>

<template>
  <section class="pick-time-action">
    <template v-if="action">
      <fieldset class="fieldset">
        <legend v-shtml="prompt" class="visually-hidden" />

        <ActionHeader :description="prompt" :title="title" />

        <TransitionGroup appear class="items" name="transition-group-item" tag="div">
          <ActionCard
            v-for="(item, index) in items"
            :key="item.id"
            :active="isSelected(item.id)"
            class="item"
            :disabled="item.isSoldOut"
            padding="compact"
            role="radio"
            :style="{ '--index': index }"
            :value="item.id"
            @click="handleSelect(item.id)"
            ><strong>{{ item.title }}</strong></ActionCard
          >
        </TransitionGroup>
      </fieldset>
    </template>

    <StatusMessage :icon="IconExclamationCircle" :message="errorMessage" variant="error" />
  </section>
</template>

<style scoped>
.pick-time-action {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: var(--space-0-5);

  .fieldset {
    display: contents;
  }

  .items {
    --_min-item-width: 120px;

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--_min-item-width), 1fr));
    gap: var(--space-1);

    .item {
      min-width: var(--_min-item-width);
    }
  }

  .items :deep(.action-card) {
    text-align: center;
  }

  .transition-group-item-enter-active {
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
    transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
  }
  .transition-group-item-enter-from {
    opacity: 0;
    transform: translateY(30px);
  }
}
</style>
